import { Button, Col, Popconfirm, Row, Space, Tabs, Typography } from "antd";
import debounce from "lodash.debounce";
import { FC, useMemo, useRef, useState } from "react";
import Icon from "../../../../../assets/icons/icon";
import CardBox from "../../../../../components/cardBox";
import CollapseCard from "../../../../../components/collapseCard";
import CopyableContent from "../../../../../components/copyableContent";
import Details from "../../../../../components/detailsList";
import DividerLine from "../../../../../components/lineSplit";
import LogText from "../../../../../components/logText";
import { renderErrorMessage } from "../../../../../components/messages/errorMessage";
import VehicleInfo from "../../../../../components/vehicleInfoViews/vehicleInfo";
import TaxCaseStore from "../../../../../contexts/taxCaseStore";
import _t from "../../../../../lang/translate";
import { taxFetchPriceDocumentation } from "../../../../../services/taxService";
import { getRemoteData, patchVehicle } from "../../../../../services/vehicleService";
import { Vehicle } from "../../../../../types/appTypes";
import format from "../../../../../utilities/formatNumbers";
import { isValidVin } from "../../../../../utilities/stringExtraFunctions";
import { isString, isValidNum } from "../../../../../utilities/typeGuard";
import openAsyncModal from "../../../motorAtlasCatalogMapper/openAsyncModal";
import TaxVehicleEditModal from "./taxVehicleEditModal";

const TaxVehicleEdit: FC = () => {
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingPriceData, setIsLoadingPriceData] = useState<boolean>(false);
  const [localDATVehicleData, setLocalDATVehicleData] = useState(taxCase.vehicle?.remoteData?.dat?.mapped);
  const [localDMRVehicleData, setLocalDMRVehicleData] = useState(taxCase.vehicle?.remoteData?.dmr?.mapped);

  const startEditing = () => setIsEditing(true);
  const stopEditing = () => setIsEditing(false);

  const fetchPriceData = async () => {
    try {
      setIsLoadingPriceData(true)
      const { data } = await taxFetchPriceDocumentation(taxCase.id)
      setTaxCase(data)
    } catch (err) {
      renderErrorMessage(err)
    } finally {
      setIsLoadingPriceData(false)
    }
  }


  return (
    <>
      <CollapseCard className="tax-vehicle-edit-collapse-card" title={_t("vehicle")}>
        <VehicleManager />
        <CardBox className="mb-1 mt-1" style={{ whiteSpace: "pre-wrap" }} title={_t("noteFromCreator")}>
          {taxCase.note || "-"}
        </CardBox>
        <Row className="mt-1" gutter={[8, 16]} justify="space-between" align="bottom">
          <Col className="d-flex gap-05 flex-wrap">
            {/* <TaxEquipmentReport /> */}
            {/* <Button loading={isLoadingPriceData} icon={<Icon size="large" name="download-outline" />} onClick={fetchPriceData}>{_t("fetch", "prices")}</Button> */}
          </Col>
          <Col flex="1" className="text-right">
            <Popconfirm title={_t("msg.this_function_is_deprecated") + "!"} onConfirm={startEditing}>
              <Button
                type="text"
                className="pl-05 pr-05"
                loading={isSaving}
              >
                {_t("edit")}
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </CollapseCard>
      {taxCase.log?.creation && (
        <LogText
          className="p-0 text-right mt--1  mb-1"
          small
          logData={taxCase.log.creation}
        />
      )}
      <TaxVehicleEditModal
        isEditing={isEditing}
        stopEditing={stopEditing}
        startEditing={startEditing}
        setTaxCase={setTaxCase}
        taxCase={taxCase}
        DATVehicleData={localDATVehicleData}
        setDATVehicleData={setLocalDATVehicleData}
        DMRVehicleData={localDMRVehicleData}
        setDMRVehicleData={setLocalDMRVehicleData}
        setIsSaving={setIsSaving}
      />

    </>
  );
};

export default TaxVehicleEdit;


const { Link } = Typography;
function VehicleManager() {
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer();
  const [loadingMotorAtlasCatalog, setLoadingMotorAtlasCatalog] = useState(false)

  async function openModelCatalog() {
    try {
      setLoadingMotorAtlasCatalog(true)
      const data = await openAsyncModal({
        views: {
          hideSourceVehicle: false,
        },
        selectedMotorAtlasId: taxCase.vehicle?.motorAtlasId,
        inputVehicle: taxCase.vehicle
      })
      if (data?.selectedMatch?.eCode) {
        const { data: vehicle } = await patchVehicle(taxCase.vehicle?.id ?? -1, { motorAtlasId: data.selectedMatch.eCode, enrich: true })
        setTaxCase({
          ...taxCase, vehicle: vehicle
        })
      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingMotorAtlasCatalog(false)
    }
  }

  return (
    <Tabs className="vehicle-tabs" moreIcon={null}>
      <Tabs.TabPane key={"overview"} tab={_t("overview_2")}>
        <VehicleInfo
          vehicle={taxCase.vehicle}
          loadingMotorAtlasCatalog={loadingMotorAtlasCatalog}
          showDMRAlerts
          showAdditionalData
          motorAtlasIdCallback={openModelCatalog}
        />
      </Tabs.TabPane>
      <Tabs.TabPane key={"start"} tab={_t("created", "with")}>
        <VehicleSourceData />
      </Tabs.TabPane>
      <Tabs.TabPane disabled={!isValidVin(taxCase.vehicle?.vin)} key={"dmr"} tab={_t("DMR", "data")}>
        <VehicleDMRSource />
      </Tabs.TabPane>
    </Tabs>
  )
}


function VehicleSourceData() {
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer();
  const { link, type, typeId, isNew, vin, firstRegDate, mileage } = taxCase.vehicleDataSource ?? {}

  async function patchVehicleKey(partial: Partial<Vehicle>) {
    try {
      const { data: vehicle } = await patchVehicle(taxCase.vehicle?.id ?? -1, { ...partial, enrich: false }) // TODO: Should enrich be true or false
      setTaxCase({
        ...taxCase, vehicle: vehicle
      })
    } catch (error) {
      renderErrorMessage(error)
    }
  }


  return (
    <Details borderLess>
      <Details.Title className="mt-0" bold >{_t("vehicle", "data")}</Details.Title>
      {link && (
        <Details.Item
          value={
            <Space size={"small"}>
              <Link ellipsis className="max-width-240" href={link} target="_blank">
                {link}
              </Link>
              <CopyButton />
            </Space>
          }
        />
      )}
      <Details.Item label={_t("type")} value={
        <Space>
          <span>{formatValue(type)}</span>
          <CopyButton onCopy={async () => patchVehicleKey({ type, typeId })} />
        </Space>
      }
      />
      <Details.Item label={_t("new", "vehicle") + "?"}
        value={
          <Space>
            <span>{format.yesOrNo(isNew)}</span>
            <CopyButton />
          </Space>
        } />
      <Details.Item
        label={_t("vin")}
        value={
          <Space>
            <CopyableContent contentToCopy={formatValue(vin)}>
              {formatValue(vin)}
            </CopyableContent>
            <CopyButton />
          </Space>

        }
      />
      <Details.Item
        label={_t("firstRegDate")}
        value={
          <Space>
            <CopyableContent contentToCopy={format.date(firstRegDate)}>
              {format.date(firstRegDate)}
            </CopyableContent>
            <CopyButton />
          </Space>

        }
      />
      <Details.Item
        label={_t("mileage")}
        value={
          <Space>
            <CopyableContent contentToCopy={String(mileage || 0)}>
              {format.milage(mileage)}
            </CopyableContent>
            <CopyButton onCopy={() => patchVehicleKey({ mileage })} />
          </Space>

        }
      />
    </Details>

  )
}

function VehicleDMRSource() {
  const { taxCase, setTaxCase } = TaxCaseStore.useContainer();
  const [loadingDmr, setLoadingDmr] = useState(false)
  const [dmr, setDmr] = useState(taxCase.vehicle?.remoteData?.dmr?.mapped ?? {})
  const { type, typeId, vin, firstRegDate, mileage, brand, model, equipmentVariant,
    engineSize, horsePower, gearbox, body, useCase, useCaseId, quality, qualityId,
    fuel, emissionAmount, emissionUnit, emissionUnitId, batteryCapacity, isHighWeight
  } = dmr;

  const payloadRef = useRef<Partial<Vehicle>>({})
  const resolversRef = useRef<(() => void)[]>([]);
  const [saving, setSaving] = useState(false);

  async function refetchDmr() {
    try {
      setLoadingDmr(true)
      const { data: { data } } = await getRemoteData({ vin: taxCase.vehicle?.vin, items: ['dmr'], forceFresh: true })
      if (data?.dmr?.mapped) {
        setDmr(data?.dmr?.mapped)
      }
    } catch (error) {
      renderErrorMessage(error)
    } finally {
      setLoadingDmr(false)
    }
  }

  async function handlePatchVehicle() {
    try {
      const { data: vehicle } = await patchVehicle(taxCase.vehicle?.id ?? -1, { ...payloadRef.current, enrich: false }) // TODO: Should enrich be true or false
      setTaxCase({
        ...taxCase, vehicle: vehicle
      })
      payloadRef.current = {}
    } catch (error) {
      renderErrorMessage(error)
    }
  }
  async function flush() {
    setSaving(true);
    try {
      await handlePatchVehicle();     // reads + clears payloadRef.current
    } finally {
      setSaving(false);
      resolversRef.current.splice(0).forEach(r => r());
    }
  }

  const debouncedFlush = useMemo(() => debounce(flush, 600), []);

  function debouncedPatchVehicle(partial: Partial<Vehicle>) {
    payloadRef.current = { ...payloadRef.current, ...partial };
    return new Promise<void>(resolve => {
      resolversRef.current.push(resolve);
      debouncedFlush();               // schedule single flush
    });
  }


  return (
    <div>
      <Details borderLess>
        <Details.Title className="mt-0" bold extra={
          <Button type="link" onClick={refetchDmr} style={{ marginRight: -4 }}
            icon={<Icon className={loadingDmr ? "animate-spin" : ""} size="large" name="clock-clockwise" />}
          />
        }> {_t("vehicle", "data")} </Details.Title>
        <Details.Item label={_t("type")} value={
          <Space>
            <span>{formatValue(type)}</span>
            <CopyButton onCopy={async () => debouncedPatchVehicle({ type, typeId })} />
          </Space>
        } />
        <Details.Item label={_t("brand")} value={
          <Space>
            <span>{formatValue(brand)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("model")} value={
          <Space>
            <span>{formatValue(model)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("variant")} value={
          <Space>
            <span>{formatValue(equipmentVariant)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item
          label={_t("firstRegDate")}
          value={
            <Space>
              <CopyableContent contentToCopy={format.date(firstRegDate)}>
                {format.date(firstRegDate)}
              </CopyableContent>
              <CopyButton onCopy={() => debouncedPatchVehicle({ firstRegDate })} />
            </Space>
          } />
        <Details.Item
          label={_t("vin")}
          value={
            <Space>
              <CopyableContent contentToCopy={formatValue(vin)}>
                {formatValue(vin)}
              </CopyableContent>
              <CopyButton />
            </Space>
          } />
        <Details.Item
          label={_t("mileage")}
          value={
            <Space>
              <CopyableContent contentToCopy={String(mileage || 0)}>
                {format.milage(mileage)}
              </CopyableContent>
              <CopyButton onCopy={() => debouncedPatchVehicle({ mileage })} />
            </Space>
          } />
        <DividerLine className="mt-05 mb-05" />
        <Details.Item label={_t("engine_size")} value={
          <Space>
            <span>{formatValue(engineSize)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("horse_power")} value={
          <Space>
            <span>{formatValue(horsePower)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("gearbox")} value={
          <Space>
            <span>{formatValue(gearbox)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("trim")} value={
          <Space>
            <span>{formatValue(body)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("car_condition")} value={
          <Space>
            <span>{formatValue(quality)}</span>
            <CopyButton onCopy={() => debouncedPatchVehicle({ quality, qualityId })} />
          </Space>
        } />
        <Details.Item label={_t("special_use")} value={
          <Space>
            <span>{formatValue(useCase)}</span>
            <CopyButton onCopy={() => debouncedPatchVehicle({ useCaseId, useCase })} />
          </Space>
        } />
        <DividerLine className="mt-05 mb-05" />
        <Details.Item label={_t("fuel")} value={
          <Space>
            <span>{formatValue(fuel)}</span>
            <CopyButton />
          </Space>
        } />
        <Details.Item label={_t("co2_emission")} value={
          <Space>
            <span>{format.emission(emissionAmount)}</span>
            <CopyButton onCopy={() => debouncedPatchVehicle({ emissionAmount })} />
          </Space>
        } />
        <Details.Item label={_t("co2_measurement")} value={
          <Space>
            <span>{formatValue(emissionUnit)}</span>
            <CopyButton onCopy={() => debouncedPatchVehicle({ emissionUnit, emissionUnitId })} />
          </Space>
        } />
        <Details.Item label={_t("battery_capacity")} value={
          <Space>
            <span>{format.batteryCapacity(batteryCapacity)}</span>
            <CopyButton onCopy={() => debouncedPatchVehicle({ batteryCapacity })} />
          </Space>
        } />
        <Details.Item label={_t("over_3000_kg")} value={
          <Space>
            <span>{format.yesOrNo(isHighWeight)}</span>
            <CopyButton onCopy={() => debouncedPatchVehicle({ isHighWeight })} />
          </Space>
        } />



      </Details>
    </div>
  )
}

function CopyButton({ onCopy }: { onCopy?: () => Promise<void> }) {
  const [loading, setLoading] = useState(false)

  async function handleCopy() {
    setLoading(true)
    await onCopy?.()
    setLoading(false)
  }

  return (
    <span style={{
      display: "inline-block",
      cursor: "pointer",
      opacity: onCopy ? 1 : 0.3,
      pointerEvents: onCopy ? "all" : "none",
      position: "relative"
    }} onClick={handleCopy}>
      {loading ? (
        <Icon className="animate-spin" size="huge" name="sync-outline" />
      ) : (
        <Icon size="huge" name="plus-square-fill" fill="#95A5AE" />
      )}
    </span>
  )
}

const formatValue = (v?: unknown | null) => {
  if (isString(v)) {
    return v.trim()
  } else if (isValidNum(v) || typeof v === 'boolean') {
    return v + "";
  } else {
    return "-";
  }
};
